import { getStoredValue, setStoredValue } from 'common'
import React, { MutableRefObject, ReactNode } from 'react'

/**
 * Creates a deep or shallow clone of an object.
 *
 * @template Type
 * @param {Type} src object to clone
 * @param {boolean} [deep=false]
 * @returns {Type} clone of src
 */
export function clone(src: any, deep = false) {
  const wm = (deep && new WeakMap()) as WeakMap<any, any>
  return (function _clone(src): any {
    if (typeof src !== 'object') {
      return src
    } else
      switch (Object.prototype.toString.call(src)) {
        case '[object Object]':
        case '[object Array]':
          if (wm && wm.has(src)) return wm.get(src)
          const isarray = Array.isArray(src)
          const dst = isarray ? [] : Object.create(Object.getPrototypeOf(src))
          const properties = Object.getOwnPropertyDescriptors(src)
          if (deep) {
            for (const [name, desc] of Object.entries(properties)) {
              if (desc.hasOwnProperty('value')) {
                properties[name] = { ...desc, value: _clone(desc.value) }
              }
            }
          }
          Object.defineProperties(dst, properties)
          if (wm) wm.set(src, dst)
          return dst
        case '[object WeakMap]':
          // weak maps can't be cloned
          return src
        case '[object Map]':
          if (deep) {
            if (wm.has(src)) return wm.get(src)
            const dst = new Map(_clone(src.entries()))
            wm.set(dst, undefined)
            return dst
          }
        // otherwise fall through
        default:
          return src && new src.constructor(src.valueOf())
      }
  })(src)
}

/**
 * Deep copy version of Object.assign
 *
 * @template Type
 * @param {Type} dst object to extend
 * @param {Type[]} args object(s) to copy into dst
 * @returns {Type} first argument
 */
export function deepCopy(dst: any, ...args: any[]) {
  args.forEach((src) => {
    ;(function _copy(dst, src) {
      for (let key in src) {
        let to = dst[key],
          from = src[key]
        if (to === from || from === undefined) continue
        if (typeof from !== 'object') {
          dst[key] = from
        } else
          switch (Object.prototype.toString.call(from)) {
            case '[object Object]':
            case '[object Array]':
              if (!to || (Object.getPrototypeOf(to) !== Object.getPrototypeOf(from) && !Array.isArray(to))) {
                to = dst[key] = Array.isArray(from) ? [] : Object.create(Object.getPrototypeOf(from))
              }
              _copy(to, from)
              break
            case '[object WeakMap]':
              break
            default:
              dst[key] = new from.constructor(from.valueOf())
          }
      }
    })(dst, src)
  })
  return dst
}

export function keyedList(list: ReactNode[]): ReactNode[] {
  return list.map((value, key) => {
    if (React.isValidElement(value)) {
      return React.cloneElement(value, { key })
    } else {
      return value
    }
  })
}

export function chainedRef<T>(...refs: React.Ref<T>[]): React.RefCallback<T> {
  return (value) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref) {
        ;(ref as MutableRefObject<T | null>).current = value
      }
    }
  }
}

export function usePersistentState(key: string, initialValue: any) {
  const [value, setValue] = React.useState(() => getStoredValue(key, initialValue))
  React.useEffect(() => setStoredValue(key, value), [key, value])
  return [value, setValue] as const
}

export function memoizedCompute(func: any) {
  let prevInput: any = null
  let prevOutput: any = null
  return function (...input: any[]) {
    if (!prevInput || input.some((value, idx) => value !== prevInput[idx])) {
      prevOutput = func(...input)
    }
    prevInput = input
    return prevOutput
  }
}

export const searchRegex = (str: string, middle: boolean) => {
  const prefix = (middle && !str.match(/^\s/) ? '' : '\\b') + '('
  const suffix = ')' + (str.match(/\s$/) ? '\\b' : '')
  str = str.trim().replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&')
  if (!str) return
  return new RegExp(prefix + str + suffix, 'i')
}

export function combineFilters<Args extends unknown[]>(...filters: ((...args: Args) => any)[]) {
  filters = filters.filter(Boolean)
  if (!filters.length) return () => true
  if (filters.length === 1) return filters[0]
  return (...args: Args) => filters.every((f) => !f || f(...args))
}

export function hashName(name: string) {
  let value = 0
  for (let i = 0; i < name.length; ++i) {
    value = (value * 33 + name.charCodeAt(i)) >>> 0
  }
  return value
}

export function hashNameLower(name: string) {
  return hashName(name.toLowerCase())
}

export function useChanged<T, Args extends any[]>(
  func: (...args: [...Args, ...Args]) => T | undefined,
  ...args: Args
): T | undefined {
  const [state, setState] = React.useState<T>()
  const prevArgs = React.useRef<Args>()
  const timeoutRef = React.useRef<number>()
  React.useLayoutEffect(() => {
    if (prevArgs.current !== undefined && args.some((arg, index) => arg !== prevArgs.current![index])) {
      const value = func(...prevArgs.current!, ...args)
      if (value !== undefined) {
        setState(value)
        timeoutRef.current = window.setTimeout(() => {
          setState(undefined)
          timeoutRef.current = undefined
        }, 10)
      }
    }
    prevArgs.current = args
    return () => {
      if (timeoutRef.current) {
        setState(undefined)
        window.clearTimeout(timeoutRef.current)
        timeoutRef.current = undefined
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, args)
  return state
}

export function nearestIndex(x: number, values: number[]) {
  // find FIRST value >= cur
  let left = 0,
    right = values.length
  while (right - left > 1) {
    const mid = (left + right - 1) >> 1
    if (values[mid] < x) {
      left = mid + 1
    } else {
      right = mid + 1
    }
  }
  if (left === 0) {
    return 0
  } else if (x >= (values[left] + values[left - 1]) * 0.5) {
    return left
  } else {
    return left - 1
  }
}

export function nearestValue(x: number, values: number[]) {
  return values[nearestIndex(x, values)]
}

export function useDisplay<T extends unknown>(func: () => Promise<T>, depends: any[]) {
  const [display, setDisplay] = React.useState<T | { error: string }>()
  React.useEffect(() => {
    let valid = true
    func().then(
      (result) => {
        if (valid) setDisplay(result)
      },
      (error) => {
        if (valid) setDisplay({ error: (error as Error).message })
      }
    )
    return () => {
      valid = false
    }
  }, depends) // eslint-disable-line react-hooks/exhaustive-deps
  return display
}

export function versionNumberToString(version: number) {
  if (version === 326) return '3.25.2'
  if (version === 3261) return '3.26'
  return `${(version / 100).toFixed(2)}`
}
